/**
 * The realtime socket (docs/specs/operations.md §9.2): one WebSocket per tab to
 * `/api/v1/realtime`, authenticated by the session cookie. It carries chat messages for the
 * conversations on screen (subscribe / unsubscribe), "something changed" nudges for the chat
 * list and badges, and the bell's unread count, so neither the bell nor chat polls.
 *
 * - Heartbeat: `{type:'ping'}` every 25s; if nothing at all arrives for 60s the socket is
 *   presumed dead and replaced.
 * - Reconnect with exponential backoff (1s → 30s, jittered); immediately when the tab becomes
 *   visible or the browser comes back online.
 * - After a reconnect every subscription is renewed and listeners get `reconnected`, so message
 *   panes fetch what they missed by cursor (`?after=<last seq>`); lists and the bell refetch.
 */
import { RealtimeServerFrameSchema, type RealtimeClientFrame } from '@aroo/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { chatKeys } from '../api/chat';
import { notificationKeys } from '../api/notifications';
import { salesKeys } from '../api/sales';
import type { NotificationsResponse } from '@aroo/shared';
import { RealtimeContext, type RealtimeEvent, type RealtimeStatus } from './realtimeContext';
import { backoffDelay, realtimeUrl } from './realtimeHelpers';

const PING_MS = 25_000;
const DEAD_AFTER_MS = 60_000;

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const ws = useRef<WebSocket | null>(null);
  const subs = useRef(new Map<string, number>());
  const listeners = useRef(new Set<(e: RealtimeEvent) => void>());
  const attempt = useRef(0);
  const lastHeard = useRef(0);
  /** The latest `connect`, for the retry timer (which is scheduled from inside it). */
  const connectRef = useRef<() => void>(() => undefined);
  const everOpened = useRef(false);
  const stopped = useRef(false);
  const retryTimer = useRef<number | null>(null);

  const send = useCallback((frame: RealtimeClientFrame) => {
    const s = ws.current;
    if (s && s.readyState === WebSocket.OPEN) s.send(JSON.stringify(frame));
  }, []);

  const emit = useCallback((e: RealtimeEvent) => {
    for (const l of listeners.current) {
      try {
        l(e);
      } catch {
        // one bad listener must not break the rest
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (stopped.current) return;
    if (retryTimer.current) {
      window.clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    const prev = ws.current;
    if (prev && (prev.readyState === WebSocket.OPEN || prev.readyState === WebSocket.CONNECTING)) {
      return;
    }
    setStatus('connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(realtimeUrl(window.location));
    } catch {
      setStatus('closed');
      return;
    }
    ws.current = socket;
    socket.onopen = () => {
      attempt.current = 0;
      lastHeard.current = Date.now();
      setStatus('open');
      for (const channelId of subs.current.keys()) {
        socket.send(JSON.stringify({ type: 'subscribe', channelId }));
      }
      if (everOpened.current) {
        // Catch up on anything missed while disconnected.
        void qc.invalidateQueries({ queryKey: chatKeys.all });
        void qc.invalidateQueries({ queryKey: notificationKeys.list });
        emit({ type: 'reconnected' });
      }
      everOpened.current = true;
    };
    socket.onmessage = (ev) => {
      lastHeard.current = Date.now();
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      const frame = RealtimeServerFrameSchema.safeParse(parsed);
      if (!frame.success) return;
      const f = frame.data;
      switch (f.type) {
        case 'notifications': {
          const known = qc.getQueryData<NotificationsResponse>(notificationKeys.list);
          if (!known || known.unread !== f.unread) {
            void qc.invalidateQueries({ queryKey: notificationKeys.list });
          }
          break;
        }
        case 'chat.activity':
        case 'chat.channels':
        case 'chat.read':
          void qc.invalidateQueries({ queryKey: chatKeys.overview });
          break;
        case 'chat.message':
        case 'chat.message.updated':
          void qc.invalidateQueries({ queryKey: chatKeys.overview });
          break;
        case 'sales.updated':
          // `sales-sync` changed today's figures: open branch pages and the Home card refetch.
          void qc.invalidateQueries({ queryKey: salesKeys.all });
          break;
        default:
          break;
      }
      emit(f);
    };
    socket.onclose = (ev) => {
      if (ws.current === socket) ws.current = null;
      setStatus('closed');
      if (stopped.current) return;
      // 4401: the session ended; the next API call will send the person to sign in.
      const delay = ev.code === 4401 ? 30_000 : backoffDelay(attempt.current);
      attempt.current += 1;
      retryTimer.current = window.setTimeout(() => connectRef.current(), delay);
    };
    socket.onerror = () => {
      // onclose follows and schedules the retry.
    };
  }, [emit, qc]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    stopped.current = false;
    // Open on the next tick (connect updates the status, which an effect must not do directly).
    const first = window.setTimeout(connect, 0);
    const ping = window.setInterval(() => {
      const s = ws.current;
      if (!s || s.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastHeard.current > DEAD_AFTER_MS) {
        s.close(4000, 'no heartbeat');
        return;
      }
      send({ type: 'ping' });
    }, PING_MS);
    const wake = () => {
      if (document.visibilityState === 'visible') {
        attempt.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    return () => {
      stopped.current = true;
      window.clearTimeout(first);
      window.clearInterval(ping);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      ws.current?.close(1000, 'bye');
      ws.current = null;
    };
  }, [connect, send]);

  const subscribe = useCallback(
    (channelId: string) => {
      const n = subs.current.get(channelId) ?? 0;
      subs.current.set(channelId, n + 1);
      if (n === 0) send({ type: 'subscribe', channelId });
      return () => {
        const left = (subs.current.get(channelId) ?? 1) - 1;
        if (left <= 0) {
          subs.current.delete(channelId);
          send({ type: 'unsubscribe', channelId });
        } else {
          subs.current.set(channelId, left);
        }
      };
    },
    [send],
  );

  const listen = useCallback((handler: (e: RealtimeEvent) => void) => {
    listeners.current.add(handler);
    return () => {
      listeners.current.delete(handler);
    };
  }, []);

  const api = useMemo(() => ({ status, subscribe, listen }), [status, subscribe, listen]);
  return <RealtimeContext.Provider value={api}>{children}</RealtimeContext.Provider>;
}
